// routes
import {
  PATH_CLIENT,
  PATH_DASHBOARD,
  PATH_LINGUIST
} from '../../../routes/paths';
// components

import Iconify from '../../../components/Iconify';
// ----------------------------------------------------------------------

// One icon family for the whole sidebar: Solar, bold-duotone, through the
// app's Iconify component (the same runtime that already draws the eva:*
// icons). The two local SVG sets it replaces — navbar1 (26 curated glyphs) and
// the older navbar — had to be mixed to cover every concept, and mixing them
// meant mixing two drawing styles. Here every entry has the glyph drawn for
// its meaning AND every glyph shares one weight, one corner radius, one
// duotone treatment. Icons inherit `currentColor`, so the tile decides the
// colour: soft at rest, white on the accent when active.
const getIcon = (name) => (
  <Iconify icon={`solar:${name}-bold-duotone`} width={20} height={20} />
);

const ICONS = {
  dashboard: getIcon('widget-5'), // dashboard tiles
  user: getIcon('users-group-rounded'), // the team
  task: getIcon('checklist-minimalistic'), // tasks
  bitAndBob: getIcon('confetti-minimalistic'), // Bits & Bobs — odds and ends
  quality: getIcon('medal-ribbons-star'), // quality cases + golden pins
  issue: getIcon('medal-ribbons-star'), // alias of quality
  company: getIcon('buildings-2'), // clients (companies)
  banking: getIcon('buildings-2'), // alias of company
  job: getIcon('case-round-minimalistic'), // vendors — a briefcase
  cart: getIcon('wallet-money'), // expenses
  invoice: getIcon('bill-list'), // invoices
  invoice2: getIcon('bill-list'), // alias of invoice
  brochure: getIcon('notebook-bookmark'), // brochures — a printed booklet
  translationProject: getIcon('translation-2'), // LY — translation
  barzanoProject: getIcon('scale'), // LY-BZ — Barzanò & Zanardo, IP law firm
  bugnionProject: getIcon('diploma-verified'), // LY-BG — Bugnion, patents
  interpretationProject: getIcon('headphones-round-sound'), // CI — interpretation, the interpreter's headset
  booking: getIcon('headphones-round-sound'), // alias of interpretationProject
  mediation: getIcon('dialog-2'), // CI-MC — mediation, two parties talking
  quote: getIcon('calculator-minimalistic'), // quotes — a priced estimate
  projects: getIcon('clipboard-list'), // projects
  project: getIcon('clipboard-list'), // alias of projects
  document: getIcon('clipboard-list'), // alias of projects
  phone: getIcon('phone-calling-rounded'), // make a call
  calendar: getIcon('calendar'), // projects calendar
  vault: getIcon('lock-keyhole-minimalistic'), // vault — secure storage
  analytics: getIcon('pie-chart-2'), // reports
  management: getIcon('graph-up'), // management — profit, costs, ROI
  support: getIcon('question-circle'), // help & support
  settings: getIcon('settings'), // settings
  profile: getIcon('user-rounded'), // the linguist's profile
  services: getIcon('hand-stars'), // services the linguist offers
  card: getIcon('card'), // payment info
  sheets: getIcon('clipboard-text'), // technician sheets
  ecommerce: getIcon('box'), // technician inventory
  formatting: getIcon('ruler-pen'), // formatting projects — layout tools
  marketingAnalysis: getIcon('chart-2'), // marketing channels analysis
  home: getIcon('home-smile'), // client home
  clientProfile: getIcon('user-circle'), // client edit profile
  orders: getIcon('bag-check'), // client orders — an order confirmed
  blog: getIcon('document-text'), // blog
  chat: getIcon('chat-round-dots'), // chat
  mail: getIcon('letter'), // mail
  communication: getIcon('letter-unread'), // communication
  ocr: getIcon('scanner'), // OCR
  kanban: getIcon('kanban'), // kanban
  menuItem: getIcon('widget-2'), // menu item
  file: getIcon('file-text'), // file
  videoConferenceProject: getIcon('videocamera'), // video conference projects
  proofReading: getIcon('file-search'), // proof-reading
  golden: getIcon('medal-star-circle'), // golden pins
  publicForm: getIcon('document-add'), // public form
  permissions: getIcon('shield-check') // permissions
};

const navConfig = [
  // MANAGEMENT
  // ----------------------------------------------------------------------
  {
    subheader: 'management',
    items: [
      // USER
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'Project Manager'
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'Finance Manager'
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: ['Account Manager', 'Business Developer']
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'admin'
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'Senior Manager'
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'Marketing Manager'
      },
      {
        title: 'Dashboard',
        path: PATH_DASHBOARD.general.app,
        icon: ICONS.dashboard,
        roles: 'HR Manager'
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'admin',
        children: [
          { title: 'cards', path: PATH_DASHBOARD.user.cards, roles: 'admin' },
          {
            title: 'create',
            role: 'admin',
            path: PATH_DASHBOARD.user.new,
            roles: 'admin'
          },
          {
            title: 'account',
            path: PATH_DASHBOARD.user.account,
            roles: 'admin'
          }
        ]
      },

      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'Senior Manager',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'Senior Manager'
          },
          {
            title: 'create',
            role: 'Senior Manager',
            path: PATH_DASHBOARD.user.new,
            roles: 'Senior Manager'
          },
          {
            title: 'account',
            path: PATH_DASHBOARD.user.account,
            roles: 'Senior Manager'
          }
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'Project Manager',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'Project Manager'
          }
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'Finance Manager',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'Finance Manager'
          },
          {
            title: 'create',
            role: 'Finance Manager',
            path: PATH_DASHBOARD.user.new,
            roles: 'Finance Manager'
          }
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: ['Account Manager', 'Business Developer']
          }
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'Marketing Manager',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'Marketing Manager'
          }
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'HR Manager',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'HR Manager'
          },
          {
            title: 'create',
            role: 'HR Manager',
            path: PATH_DASHBOARD.user.new,
            roles: 'HR Manager'
          }
          // { title: 'account', path: PATH_DASHBOARD.user.account, roles: 'HR Manager' },
        ]
      },
      {
        title: 'team',
        path: PATH_DASHBOARD.user.root,
        icon: ICONS.user,
        roles: 'Software Developer',
        children: [
          {
            title: 'cards',
            path: PATH_DASHBOARD.user.cards,
            roles: 'Software Developer'
          }
        ]
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Project Manager'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Finance Manager'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'admin'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Senior Manager'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Marketing Manager'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'HR Manager'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Software Developer'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Business Developer'
      },
      {
        title: 'tasks',
        icon: ICONS.task,
        path: PATH_DASHBOARD.task.list,
        roles: 'Account Manager'
      },
      // BITS & BOBS — shared quick-reminder board
      {
        title: 'Bits & Bobs',
        icon: ICONS.bitAndBob,
        path: PATH_DASHBOARD.bitAndBob.root,
        roles: [
          'admin',
          'Project Manager',
          'Account Manager',
          'Finance Manager',
          'Senior Manager',
          'Marketing Manager',
          'HR Manager',
          'Software Developer',
          'Business Developer'
        ]
      },
      // QUALITY — Quality Cases + Golden Pin grouped under one parent to
      // reduce the number of top-level menu items.
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'admin',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'Senior Manager',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'Project Manager',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'Project Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: 'Project Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'Finance Manager',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'Finance Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: 'Finance Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: ['Account Manager', 'Business Developer'],
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: ['Account Manager', 'Business Developer'],
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: ['Account Manager', 'Business Developer'],
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'HR Manager',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'HR Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.issue.list },
              { title: 'create', path: PATH_DASHBOARD.issue.new }
            ]
          },
          {
            title: 'Golden Pins',
            icon: ICONS.golden,
            path: PATH_DASHBOARD.golden.root,
            roles: 'HR Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.golden.list },
              { title: 'create', path: PATH_DASHBOARD.golden.new }
            ]
          }
        ]
      },
      {
        title: 'Quality',
        icon: ICONS.quality,
        path: '#1',
        roles: 'Software Developer',
        children: [
          {
            title: 'Quality Cases',
            icon: ICONS.quality,
            path: PATH_DASHBOARD.issue.root,
            roles: 'Software Developer',
            children: [{ title: 'list', path: PATH_DASHBOARD.issue.list }]
          }
        ]
      },
      // COMPANY
      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          {
            title: 'My Portfolio',
            path: PATH_DASHBOARD.company.amPortfolio,
            roles: 'Account Manager'
          },
          {
            title: 'My Opportunities',
            path: PATH_DASHBOARD.company.bdOpportunities,
            roles: 'Business Developer'
          },
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },

      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: 'admin',
        children: [
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },
      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: 'Senior Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },
      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },
      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },
      {
        title: 'clients',
        icon: ICONS.banking,
        path: PATH_DASHBOARD.company.root,
        roles: 'HR Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.company.list },
          { title: 'create', path: PATH_DASHBOARD.company.new }
        ]
      },
      // LINGUIST
      {
        title: 'Vendors',
        icon: ICONS.job,
        path: PATH_DASHBOARD.vendor.root,
        roles: 'Project Manager',
        children: [
          { title: 'Translators', path: PATH_DASHBOARD.vendor.translator },
          // Interpreters and mediators are booked far more often than
          // formatters, so they sit next to the translators.
          { title: 'Interpreters', path: PATH_DASHBOARD.vendor.interpreter },
          { title: 'Mediators', path: PATH_DASHBOARD.vendor.mediator },
          { title: 'Hostess', path: PATH_DASHBOARD.vendor.hostess },
          { title: 'Mediator billing', path: PATH_DASHBOARD.vendor.mediatorBilling },
          { title: 'Formatters', path: PATH_DASHBOARD.vendor.formatter },
          { title: 'Technicians', path: PATH_DASHBOARD.vendor.technician },
          { title: 'Blacklisted', path: PATH_DASHBOARD.vendor.blacklisted },
          // vendors deleted for good: who did it, when and why (29/10/2026)
          { title: 'Deleted', path: PATH_DASHBOARD.vendor.deleted },
          {
            title: 'Incomplete Profile',
            path: PATH_DASHBOARD.vendor.incomplete
          }
        ]
      },
      {
        title: 'Vendors',
        icon: ICONS.job,
        path: PATH_DASHBOARD.vendor.root,
        roles: 'Finance Manager',
        children: [
          { title: 'Translators', path: PATH_DASHBOARD.vendor.translator },
          // Interpreters and mediators are booked far more often than
          // formatters, so they sit next to the translators.
          { title: 'Interpreters', path: PATH_DASHBOARD.vendor.interpreter },
          { title: 'Mediators', path: PATH_DASHBOARD.vendor.mediator },
          { title: 'Hostess', path: PATH_DASHBOARD.vendor.hostess },
          { title: 'Mediator billing', path: PATH_DASHBOARD.vendor.mediatorBilling },
          { title: 'Formatters', path: PATH_DASHBOARD.vendor.formatter },
          { title: 'Technicians', path: PATH_DASHBOARD.vendor.technician },
          { title: 'Blacklisted', path: PATH_DASHBOARD.vendor.blacklisted },
          // vendors deleted for good: who did it, when and why (29/10/2026)
          { title: 'Deleted', path: PATH_DASHBOARD.vendor.deleted },
          {
            title: 'Incomplete Profile',
            path: PATH_DASHBOARD.vendor.incomplete
          }
        ]
      },
      {
        title: 'Vendors',
        icon: ICONS.job,
        path: PATH_DASHBOARD.vendor.root,
        roles: 'admin',
        children: [
          { title: 'Translators', path: PATH_DASHBOARD.vendor.translator },
          // Interpreters and mediators are booked far more often than
          // formatters, so they sit next to the translators.
          { title: 'Interpreters', path: PATH_DASHBOARD.vendor.interpreter },
          { title: 'Mediators', path: PATH_DASHBOARD.vendor.mediator },
          { title: 'Hostess', path: PATH_DASHBOARD.vendor.hostess },
          { title: 'Mediator billing', path: PATH_DASHBOARD.vendor.mediatorBilling },
          { title: 'Formatters', path: PATH_DASHBOARD.vendor.formatter },
          { title: 'Technicians', path: PATH_DASHBOARD.vendor.technician },
          { title: 'Blacklisted', path: PATH_DASHBOARD.vendor.blacklisted },
          // vendors deleted for good: who did it, when and why (29/10/2026)
          { title: 'Deleted', path: PATH_DASHBOARD.vendor.deleted },
          {
            title: 'Incomplete Profile',
            path: PATH_DASHBOARD.vendor.incomplete
          }
        ]
      },
      {
        title: 'Vendors',
        icon: ICONS.job,
        path: PATH_DASHBOARD.vendor.root,
        roles: 'Senior Manager',
        children: [
          { title: 'Translators', path: PATH_DASHBOARD.vendor.translator },
          // Interpreters and mediators are booked far more often than
          // formatters, so they sit next to the translators.
          { title: 'Interpreters', path: PATH_DASHBOARD.vendor.interpreter },
          { title: 'Mediators', path: PATH_DASHBOARD.vendor.mediator },
          { title: 'Hostess', path: PATH_DASHBOARD.vendor.hostess },
          { title: 'Mediator billing', path: PATH_DASHBOARD.vendor.mediatorBilling },
          { title: 'Formatters', path: PATH_DASHBOARD.vendor.formatter },
          { title: 'Technicians', path: PATH_DASHBOARD.vendor.technician },
          { title: 'Blacklisted', path: PATH_DASHBOARD.vendor.blacklisted },
          // vendors deleted for good: who did it, when and why (29/10/2026)
          { title: 'Deleted', path: PATH_DASHBOARD.vendor.deleted },
          {
            title: 'Incomplete Profile',
            path: PATH_DASHBOARD.vendor.incomplete
          }
        ]
      },
      {
        title: 'Vendors',
        icon: ICONS.job,
        path: PATH_DASHBOARD.vendor.root,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          { title: 'Translators', path: PATH_DASHBOARD.vendor.translator },
          // Interpreters and mediators are booked far more often than
          // formatters, so they sit next to the translators.
          { title: 'Interpreters', path: PATH_DASHBOARD.vendor.interpreter },
          { title: 'Mediators', path: PATH_DASHBOARD.vendor.mediator },
          { title: 'Hostess', path: PATH_DASHBOARD.vendor.hostess },
          { title: 'Mediator billing', path: PATH_DASHBOARD.vendor.mediatorBilling },
          { title: 'Formatters', path: PATH_DASHBOARD.vendor.formatter },
          { title: 'Technicians', path: PATH_DASHBOARD.vendor.technician },
          { title: 'Blacklisted', path: PATH_DASHBOARD.vendor.blacklisted },
          // vendors deleted for good: who did it, when and why (29/10/2026)
          { title: 'Deleted', path: PATH_DASHBOARD.vendor.deleted },
          {
            title: 'Incomplete Profile',
            path: PATH_DASHBOARD.vendor.incomplete
          }
        ]
      },
      // Expenses
      {
        title: 'Expenses',
        icon: ICONS.cart,
        path: PATH_DASHBOARD.expense.root,
        roles: 'admin',
        children: [
          { title: 'list', path: PATH_DASHBOARD.expense.list },
          { title: 'create', path: PATH_DASHBOARD.expense.new },
          { title: 'budget', path: PATH_DASHBOARD.expense.budget }
        ]
      },
      {
        title: 'Expenses',
        icon: ICONS.cart,
        path: PATH_DASHBOARD.expense.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.expense.list },
          { title: 'create', path: PATH_DASHBOARD.expense.new },
          { title: 'budget', path: PATH_DASHBOARD.expense.budget }
        ]
      },
      // {
      //   title: 'Expenses',
      //   icon: ICONS.cart,
      //   path: PATH_DASHBOARD.expense.root,
      //   roles: 'Account Manager',
      //   children: [
      //     { title: 'list', path: PATH_DASHBOARD.expense.list },
      //     { title: 'create', path: PATH_DASHBOARD.expense.new },
      //   ],
      // },
      // Invoice
      {
        title: 'Invoices',
        icon: ICONS.invoice,
        path: PATH_DASHBOARD.invoice.root,
        roles: 'Project Manager',
        children: [
          { title: 'Client', path: PATH_DASHBOARD.invoice.listClientInvoice },
          { title: 'Vendor', path: PATH_DASHBOARD.invoice.list },
          {
            title: 'Finance Billing',
            path: PATH_DASHBOARD.invoice.financeBilling
          }
        ]
      },
      {
        title: 'Invoices',
        icon: ICONS.invoice,
        path: PATH_DASHBOARD.invoice.root,
        roles: 'Finance Manager',
        children: [
          { title: 'Client', path: PATH_DASHBOARD.invoice.listClientInvoice },
          { title: 'Vendor', path: PATH_DASHBOARD.invoice.list },
          {
            title: 'Finance Billing',
            path: PATH_DASHBOARD.invoice.financeBilling
          }
        ]
      },
      {
        title: 'Invoices',
        icon: ICONS.invoice,
        path: PATH_DASHBOARD.invoice.root,
        roles: 'admin',
        children: [
          { title: 'Client', path: PATH_DASHBOARD.invoice.listClientInvoice },
          { title: 'Vendor', path: PATH_DASHBOARD.invoice.list },
          {
            title: 'Finance Billing',
            path: PATH_DASHBOARD.invoice.financeBilling
          }
        ]
      },
      {
        title: 'Invoices',
        icon: ICONS.invoice,
        path: PATH_DASHBOARD.invoice.root,
        roles: 'Senior Manager',
        children: [
          { title: 'Client', path: PATH_DASHBOARD.invoice.listClientInvoice },
          { title: 'Vendor', path: PATH_DASHBOARD.invoice.list },
          {
            title: 'Finance Billing',
            path: PATH_DASHBOARD.invoice.financeBilling
          }
        ]
      },
      {
        title: 'Invoices',
        icon: ICONS.invoice,
        path: PATH_DASHBOARD.invoice.root,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          { title: 'Client', path: PATH_DASHBOARD.invoice.listClientInvoice },
          { title: 'Vendor', path: PATH_DASHBOARD.invoice.list },
          {
            title: 'Finance Billing',
            path: PATH_DASHBOARD.invoice.financeBilling
          }
        ]
      },

      {
        title: 'Brochures',
        icon: ICONS.brochure,
        path: PATH_DASHBOARD.brochure.root,
        roles: 'admin',
        children: [
          { title: 'list', path: PATH_DASHBOARD.brochure.list },
          { title: 'create', path: PATH_DASHBOARD.brochure.new },
          { title: 'AI Maker', path: PATH_DASHBOARD.brochure.ai }
        ]
      },
      {
        title: 'Brochures',
        icon: ICONS.brochure,
        path: PATH_DASHBOARD.brochure.root,
        roles: 'Senior Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.brochure.list },
          { title: 'create', path: PATH_DASHBOARD.brochure.new },
          { title: 'AI Maker', path: PATH_DASHBOARD.brochure.ai }
        ]
      },
      {
        title: 'Brochures',
        icon: ICONS.brochure,
        path: PATH_DASHBOARD.brochure.root,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.brochure.list },
          { title: 'create', path: PATH_DASHBOARD.brochure.new },
          { title: 'AI Maker', path: PATH_DASHBOARD.brochure.ai }
        ]
      },

      // BARZANO PROJECT

      {
        title: 'LY-BZ',
        icon: ICONS.barzanoProject,
        path: PATH_DASHBOARD.barzanoProject.root,
        roles: 'Project Manager',
        children: [{ title: 'list', path: PATH_DASHBOARD.barzanoProject.list }]
      },
      {
        title: 'LY-BG',
        icon: ICONS.bugnionProject,
        path: PATH_DASHBOARD.bugnionProject.root,
        roles: 'Project Manager',
        children: [{ title: 'list', path: PATH_DASHBOARD.bugnionProject.list }]
      },

      {
        title: 'LY-BZ',
        icon: ICONS.barzanoProject,
        path: PATH_DASHBOARD.barzanoProject.root,
        roles: 'Finance Manager',
        children: [{ title: 'list', path: PATH_DASHBOARD.barzanoProject.list }]
      },
      {
        title: 'LY-BG',
        icon: ICONS.bugnionProject,
        path: PATH_DASHBOARD.bugnionProject.root,
        roles: 'Finance Manager',
        children: [{ title: 'list', path: PATH_DASHBOARD.bugnionProject.list }]
      },
      // TRANSLATION PROJECT
      {
        title: 'LY',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.translationProject.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.translationProject.list },
          { title: 'create', path: PATH_DASHBOARD.translationProject.new }
        ]
      },
      {
        title: 'LY',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.translationProject.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.translationProject.list },
          { title: 'create', path: PATH_DASHBOARD.translationProject.new }
        ]
      },
      // {
      //   title: 'Requests',
      //   icon: ICONS.publicForm,
      //   path: PATH_DASHBOARD.requests.list,
      //   roles: 'Account Manager'
      // },
      {
        title: 'LY',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.translationQuote.root,
        roles: ['Account Manager'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.translationQuote.list },
          { title: 'create', path: PATH_DASHBOARD.translationQuote.new }
        ]
      },
      {
        title: 'LY-ACA',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.academyProject.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.academyProject.list },
          { title: 'create', path: PATH_DASHBOARD.academyProject.new }
        ]
      },
      {
        title: 'LY-ACA',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.academyProject.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.academyProject.list },
          { title: 'create', path: PATH_DASHBOARD.academyProject.new }
        ]
      },
      {
        title: 'LY-ACA',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.academyQuote.root,
        roles: ['Account Manager'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.academyQuote.list },
          { title: 'create', path: PATH_DASHBOARD.academyQuote.new }
        ]
      },
      {
        title: 'LY-DEV',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.softwaredevProject.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.softwaredevProject.list },
          { title: 'create', path: PATH_DASHBOARD.softwaredevProject.new }
        ]
      },
      {
        title: 'LY-DEV',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.softwaredevProject.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.softwaredevProject.list },
          { title: 'create', path: PATH_DASHBOARD.softwaredevProject.new }
        ]
      },
      {
        title: 'LY-DEV',
        icon: ICONS.translationProject,
        path: PATH_DASHBOARD.softwaredevQuote.root,
        roles: ['Account Manager'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.softwaredevQuote.list },
          { title: 'create', path: PATH_DASHBOARD.softwaredevQuote.new }
        ]
      },
      {
        title: 'CI',
        icon: ICONS.interpretationProject,
        path: PATH_DASHBOARD.interpretationProject.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.interpretationProject.list },
          { title: 'create', path: PATH_DASHBOARD.interpretationProject.new },
          { title: 'Inventory', path: PATH_DASHBOARD.inventory.list }
        ]
      },
      {
        title: 'CI-MC',
        icon: ICONS.mediation,
        path: PATH_DASHBOARD.mediationProject.root,
        roles: 'Project Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.mediationProject.list },
          { title: 'create', path: PATH_DASHBOARD.mediationProject.new }
        ]
      },
      {
        title: 'CI',
        icon: ICONS.interpretationProject,
        path: PATH_DASHBOARD.interpretationProject.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.interpretationProject.list },
          { title: 'create', path: PATH_DASHBOARD.interpretationProject.new }
        ]
      },
      {
        title: 'CI-MC',
        icon: ICONS.mediation,
        path: PATH_DASHBOARD.mediationProject.root,
        roles: 'Finance Manager',
        children: [
          { title: 'list', path: PATH_DASHBOARD.mediationProject.list },
          { title: 'create', path: PATH_DASHBOARD.mediationProject.new }
        ]
      },
      {
        title: 'CI',
        icon: ICONS.interpretationProject,
        path: PATH_DASHBOARD.interpretationQuote.root,
        roles: ['Account Manager'],
        children: [
          { title: 'list', path: PATH_DASHBOARD.interpretationQuote.list },
          { title: 'create', path: PATH_DASHBOARD.interpretationQuote.new }
        ]
      },

      // {
      //   title: 'Requests',
      //   icon: ICONS.publicForm,
      //   path: PATH_DASHBOARD.requests.list,
      //   roles: 'admin'
      // },
      {
        title: 'Quotes',
        path: '#1',
        roles: 'admin',
        icon: ICONS.quote,
        children: [
          {
            title: 'LY',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.translationQuote.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.translationQuote.list },
              { title: 'create', path: PATH_DASHBOARD.translationQuote.new }
            ]
          },
          {
            title: 'LY-ACA',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.academyQuote.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.academyQuote.list },
              { title: 'create', path: PATH_DASHBOARD.academyQuote.new }
            ]
          },
          {
            title: 'LY-DEV',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.softwaredevQuote.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.softwaredevQuote.list },
              { title: 'create', path: PATH_DASHBOARD.softwaredevQuote.new }
            ]
          },
          {
            title: 'CI',
            icon: ICONS.interpretationProject,
            path: PATH_DASHBOARD.interpretationQuote.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.interpretationQuote.list },
              { title: 'create', path: PATH_DASHBOARD.interpretationQuote.new }
            ]
          }
        ]
      },
      // {
      //   title: 'Requests',
      //   icon: ICONS.publicForm,
      //   path: PATH_DASHBOARD.requests.list,
      //   roles: 'Senior Manager'
      // },
      {
        title: 'Quotes',
        path: '#1',
        roles: 'Senior Manager',
        icon: ICONS.quote,
        children: [
          {
            title: 'LY',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.translationQuote.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.translationQuote.list },
              { title: 'create', path: PATH_DASHBOARD.translationQuote.new }
            ]
          },
          {
            title: 'LY-ACA',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.academyQuote.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.academyQuote.list },
              { title: 'create', path: PATH_DASHBOARD.academyQuote.new }
            ]
          },
          {
            title: 'LY-DEV',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.softwaredevQuote.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.softwaredevQuote.list },
              { title: 'create', path: PATH_DASHBOARD.softwaredevQuote.new }
            ]
          },
          {
            title: 'CI',
            icon: ICONS.interpretationProject,
            path: PATH_DASHBOARD.interpretationQuote.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.interpretationQuote.list },
              { title: 'create', path: PATH_DASHBOARD.interpretationQuote.new }
            ]
          }
        ]
      },
      {
        title: 'Projects',
        path: '#1',
        roles: 'admin',
        icon: ICONS.document,
        children: [
          {
            title: 'LY',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.translationProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.translationProject.list },
              { title: 'create', path: PATH_DASHBOARD.translationProject.new }
            ]
          },
          {
            title: 'LY-ACA',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.academyProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.academyProject.list },
              { title: 'create', path: PATH_DASHBOARD.academyProject.new }
            ]
          },
          {
            title: 'LY-DEV',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.softwaredevProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.softwaredevProject.list },
              { title: 'create', path: PATH_DASHBOARD.softwaredevProject.new }
            ]
          },
          {
            title: 'LY-BZ',
            icon: ICONS.barzanoProject,
            path: PATH_DASHBOARD.barzanoProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.barzanoProject.list },
              { title: 'create', path: PATH_DASHBOARD.barzanoProject.new }
            ]
          },
          {
            title: 'LY-BG',
            icon: ICONS.bugnionProject,
            path: PATH_DASHBOARD.bugnionProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.bugnionProject.list },
              { title: 'create', path: PATH_DASHBOARD.bugnionProject.new }
            ]
          },
          {
            title: 'CI',
            icon: ICONS.interpretationProject,
            path: PATH_DASHBOARD.interpretationProject.root,
            roles: 'admin',
            children: [
              {
                title: 'list',
                path: PATH_DASHBOARD.interpretationProject.list
              },
              {
                title: 'create',
                path: PATH_DASHBOARD.interpretationProject.new
              },
              { title: 'Inventory', path: PATH_DASHBOARD.inventory.list }
            ]
          },
          {
            title: 'CI-MC',
            icon: ICONS.mediation,
            path: PATH_DASHBOARD.mediationProject.root,
            roles: 'admin',
            children: [
              { title: 'list', path: PATH_DASHBOARD.mediationProject.list },
              { title: 'create', path: PATH_DASHBOARD.mediationProject.new }
            ]
          }
        ]
      },
      {
        title: 'Projects',
        path: '#1',
        roles: 'Senior Manager',
        icon: ICONS.document,
        children: [
          {
            title: 'LY',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.translationProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.translationProject.list },
              { title: 'create', path: PATH_DASHBOARD.translationProject.new }
            ]
          },
          {
            title: 'LY-ACA',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.academyProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.academyProject.list },
              { title: 'create', path: PATH_DASHBOARD.academyProject.new }
            ]
          },
          {
            title: 'LY-DEV',
            icon: ICONS.translationProject,
            path: PATH_DASHBOARD.softwaredevProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.softwaredevProject.list },
              { title: 'create', path: PATH_DASHBOARD.softwaredevProject.new }
            ]
          },
          {
            title: 'LY-BZ',
            icon: ICONS.barzanoProject,
            path: PATH_DASHBOARD.barzanoProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.barzanoProject.list },
              { title: 'create', path: PATH_DASHBOARD.barzanoProject.new }
            ]
          },
          {
            title: 'LY-BG',
            icon: ICONS.bugnionProject,
            path: PATH_DASHBOARD.bugnionProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.bugnionProject.list },
              { title: 'create', path: PATH_DASHBOARD.bugnionProject.new }
            ]
          },
          {
            title: 'CI',
            icon: ICONS.interpretationProject,
            path: PATH_DASHBOARD.interpretationProject.root,
            roles: 'Senior Manager',
            children: [
              {
                title: 'list',
                path: PATH_DASHBOARD.interpretationProject.list
              },
              {
                title: 'create',
                path: PATH_DASHBOARD.interpretationProject.new
              },
              { title: 'Inventory', path: PATH_DASHBOARD.inventory.list }
            ]
          },
          {
            title: 'CI-MC',
            icon: ICONS.mediation,
            path: PATH_DASHBOARD.mediationProject.root,
            roles: 'Senior Manager',
            children: [
              { title: 'list', path: PATH_DASHBOARD.mediationProject.list },
              { title: 'create', path: PATH_DASHBOARD.mediationProject.new }
            ]
          }
        ]
      },
      //Text To SPeech
      // {
      //   title: 'Text To Speech',
      //   icon: ICONS.calendar,
      //   path: PATH_DASHBOARD.textToSpeech.text,
      //   roles: 'Project Manager',
      // },
      // {
      //   title: 'Text To Speech',
      //   icon: ICONS.calendar,
      //   path: PATH_DASHBOARD.textToSpeech.text,
      //   roles: 'Account Manager',
      // },
      // {
      //   title: 'Text To Speech',
      //   icon: ICONS.calendar,
      //   path: PATH_DASHBOARD.textToSpeech.text,
      //   roles: 'admin',
      // },
      //CALLS
      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: 'admin'
        // children: [
        //   { title: 'report', path: PATH_DASHBOARD.Calls.record },
        //   { title: 'call', path: PATH_DASHBOARD.Calls.call },
        // ],
      },
      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: 'Senior Manager'
        // children: [
        //   { title: 'report', path: PATH_DASHBOARD.Calls.record },
        //   { title: 'call', path: PATH_DASHBOARD.Calls.call },
        // ],
      },
      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: 'Project Manager'
        // children: [
        //   { title: 'report', path: PATH_DASHBOARD.Calls.record },
        //   { title: 'call', path: PATH_DASHBOARD.Calls.call },
        // ],
      },
      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: 'Finance Manager'
        // children: [
        //   { title: 'report', path: PATH_DASHBOARD.Calls.record },
        //   { title: 'call', path: PATH_DASHBOARD.Calls.call },
        // ],
      },
      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: ['Account Manager', 'Business Developer']
        // children: [
        //   { title: 'report', path: PATH_DASHBOARD.Calls.record },
        //   { title: 'call', path: PATH_DASHBOARD.Calls.call },
        // ],
      },

      {
        title: 'Make A Call',
        icon: ICONS.phone,
        path: PATH_DASHBOARD.Calls.call,
        roles: 'HR Manager'
      },
      {
        title: 'projects calendar',
        icon: ICONS.calendar,
        path: PATH_DASHBOARD.calendar,
        roles: 'Project Manager'
      },
      {
        title: 'projects calendar',
        icon: ICONS.calendar,
        path: PATH_DASHBOARD.calendar,
        roles: 'Finance Manager'
      },
      {
        title: 'projects calendar',
        icon: ICONS.calendar,
        path: PATH_DASHBOARD.calendar,
        roles: ['Account Manager', 'Business Developer']
      },

      // {
      //   title: 'Barzano OCR',
      //   path: PATH_DASHBOARD.Ocr.report,
      //   icon: ICONS.ocr,
      //   roles: 'admin',
      // },
      // {
      //   title: 'Barzano OCR',
      //   path: PATH_DASHBOARD.Ocr.report,
      //   icon: ICONS.ocr,
      //   roles: 'Project Manager',
      // },
      {
        title: 'Vault',
        icon: ICONS.vault,
        path: PATH_DASHBOARD.vault,
        roles: 'Project Manager'
      },
      {
        title: 'Vault',
        icon: ICONS.vault,
        path: PATH_DASHBOARD.vault,
        roles: 'Finance Manager'
      },
      {
        title: 'Vault',
        icon: ICONS.vault,
        path: PATH_DASHBOARD.vault,
        roles: 'admin'
      },
      {
        title: 'Vault',
        icon: ICONS.vault,
        path: PATH_DASHBOARD.vault,
        roles: 'Senior Manager'
      },
      // TASK

      {
        title: 'Reports',
        icon: ICONS.analytics,
        path: PATH_DASHBOARD.reports.root,
        roles: 'admin',
        children: [
          { title: 'Patents', path: PATH_DASHBOARD.reports.patentReports },
          { title: 'Team Activity', path: PATH_DASHBOARD.reports.callRecords },
          { title: 'Private Client', path: PATH_DASHBOARD.reports.privateClients },
          {
            title: 'General Orders List',
            path: PATH_DASHBOARD.reports.allGeneralProjectsList
          },
          {
            title: 'Marketing channels',
            path: PATH_DASHBOARD.reports.marketingChannelList
          },
          {
            title: 'Quotes conversion',
            path: PATH_DASHBOARD.reports.quotesConversionReports
          },
          {
            title: 'Quality cases',
            path: PATH_DASHBOARD.reports.issuesReports
          },
          {
            title: 'Revenue analysis',
            path: PATH_DASHBOARD.reports.totalsalesReport
          },
          { title: 'Expenses', path: PATH_DASHBOARD.reports.expenseReport },
          {
            title: 'Golden Pins',
            path: PATH_DASHBOARD.reports.goldenPinReports
          }
        ]
      },
      {
        title: 'Management',
        icon: ICONS.management,
        path: PATH_DASHBOARD.management,
        roles: ['admin', 'Finance Manager', 'Senior Manager'],
        children: [
          {
            title: 'Profit',
            path: PATH_DASHBOARD.management,
            roles: ['admin']
          },
          {
            title: 'Costs',
            path: PATH_DASHBOARD.reports.costReport,
            roles: ['admin', 'Finance Manager']
          },
          {
            title: 'Marketing ROI',
            path: PATH_DASHBOARD.reports.ROIAnalysis,
            roles: ['admin']
          },
          {
            title: 'Credit Control',
            path: PATH_DASHBOARD.reports.clientCashFlows,
            roles: ['admin', 'Finance Manager', 'Senior Manager']
          },
          {
            title: 'AM Reports',
            path: PATH_DASHBOARD.reports.accountManagersReports,
            roles: ['admin']
          },
          {
            title: 'PM Reports',
            path: PATH_DASHBOARD.reports.pmReports,
            roles: ['admin']
          }
        ]
      },
      {
        title: 'Reports',
        icon: ICONS.analytics,
        path: PATH_DASHBOARD.reports.root,
        roles: 'Senior Manager',
        children: [
          { title: 'Patents', path: PATH_DASHBOARD.reports.patentReports },
          { title: 'Team Activity', path: PATH_DASHBOARD.reports.callRecords },
          { title: 'Private Client', path: PATH_DASHBOARD.reports.privateClients },
          {
            title: 'General Orders List',
            path: PATH_DASHBOARD.reports.allGeneralProjectsList
          },
          {
            title: 'Marketing channels',
            path: PATH_DASHBOARD.reports.marketingChannelList
          },
          {
            title: 'Quotes conversion',
            path: PATH_DASHBOARD.reports.quotesConversionReports
          },
          {
            title: 'Quality cases',
            path: PATH_DASHBOARD.reports.issuesReports
          },
          {
            title: 'Revenue analysis',
            path: PATH_DASHBOARD.reports.totalsalesReport
          }
        ]
      },
      {
        title: 'Reports',
        icon: ICONS.analytics,
        path: PATH_DASHBOARD.reports.root,
        roles: 'Project Manager',
        children: [
          { title: 'Patents', path: PATH_DASHBOARD.reports.patentReports },
          { title: 'Team Activity', path: PATH_DASHBOARD.reports.callRecords },
          { title: 'Private Client', path: PATH_DASHBOARD.reports.privateClients },
          {
            title: 'General Orders List',
            path: PATH_DASHBOARD.reports.allGeneralProjectsList
          },
          {
            title: 'Marketing channels',
            path: PATH_DASHBOARD.reports.marketingChannelList
          },
          {
            title: 'Quotes conversion',
            path: PATH_DASHBOARD.reports.quotesConversionReports
          },
          {
            title: 'Quality cases',
            path: PATH_DASHBOARD.reports.issuesReports
          },
          {
            title: 'Revenue analysis',
            path: PATH_DASHBOARD.reports.totalsalesReport
          }
        ]
      },
      {
        title: 'Reports',
        icon: ICONS.analytics,
        path: PATH_DASHBOARD.reports.root,
        roles: 'Finance Manager',
        children: [
          { title: 'Patents', path: PATH_DASHBOARD.reports.patentReports },
          { title: 'Team Activity', path: PATH_DASHBOARD.reports.callRecords },
          { title: 'Private Client', path: PATH_DASHBOARD.reports.privateClients },
          {
            title: 'General Orders List',
            path: PATH_DASHBOARD.reports.allGeneralProjectsList
          },
          {
            title: 'Marketing channels',
            path: PATH_DASHBOARD.reports.marketingChannelList
          },
          {
            title: 'Quotes conversion',
            path: PATH_DASHBOARD.reports.quotesConversionReports
          },
          {
            title: 'Quality cases',
            path: PATH_DASHBOARD.reports.issuesReports
          },
          {
            title: 'Revenue analysis',
            path: PATH_DASHBOARD.reports.totalsalesReport
          },
          { title: 'Expenses', path: PATH_DASHBOARD.reports.expenseReport }
        ]
      },
      {
        title: 'Reports',
        icon: ICONS.analytics,
        path: PATH_DASHBOARD.reports.root,
        roles: ['Account Manager', 'Business Developer'],
        children: [
          { title: 'Patents', path: PATH_DASHBOARD.reports.patentReports },
          { title: 'Team Activity', path: PATH_DASHBOARD.reports.callRecords },
          { title: 'Private Client', path: PATH_DASHBOARD.reports.privateClients },
          {
            title: 'General Orders List',
            path: PATH_DASHBOARD.reports.allGeneralProjectsList
          },
          {
            title: 'Marketing channels',
            path: PATH_DASHBOARD.reports.marketingChannelList
          },
          {
            title: 'Quotes conversion',
            path: PATH_DASHBOARD.reports.quotesConversionReports
          },
          {
            title: 'Quality cases',
            path: PATH_DASHBOARD.reports.issuesReports
          },
          {
            title: 'Revenue analysis',
            path: PATH_DASHBOARD.reports.totalsalesReport
          }
        ]
      },
      {
        title: 'Help & Support',
        icon: ICONS.support,
        roles: [
          'admin',
          'Project Manager',
          'Account Manager',
          'Finance Manager',
          'Senior Manager',
          'Software Developer',
          'HR Manager',
          'Business Developer'
        ],
        children: [
          {
            title: 'Kanban',
            path: PATH_DASHBOARD.ticket.kanban,
            roles: ['Software Developer']
          },
          {
            title: 'Raise a ticket',
            path: PATH_DASHBOARD.ticket.support,
            roles: [
              'admin',
              'Project Manager',
              'Account Manager',
              'Finance Manager',
              'Senior Manager',
              'HR Manager',
              'Business Developer'
            ]
          },
          {
            title: 'Tickets',
            path: PATH_DASHBOARD.ticket.list,
            roles: [
              'admin',
              'Project Manager',
              'Account Manager',
              'Finance Manager',
              'Senior Manager',
              'HR Manager',
              'Business Developer'
            ]
          }
        ]
      },
      {
        title: 'Dashboard',
        path: PATH_LINGUIST.user.dashboard,
        icon: ICONS.dashboard,
        roles: 'Linguist'
      },
      {
        title: 'Profile',
        icon: ICONS.profile,
        path: PATH_LINGUIST.user.profile,
        roles: 'Linguist'
      },
      {
        title: 'Services',
        icon: ICONS.services,
        path: PATH_LINGUIST.user.services,
        roles: 'Linguist'
      },
      // {
      //   title: 'Invoice',
      //   icon: ICONS.invoice,
      //   path: PATH_LINGUIST.user.invoice,
      //   roles: "Linguist",
      // },
      {
        title: 'Invoices',
        icon: ICONS.invoice2,
        // path: PATH_LINGUIST.user.root,
        roles: 'Linguist',
        children: [
          { title: 'list', path: PATH_LINGUIST.user.invoice },
          { title: 'create', path: PATH_LINGUIST.user.invoiceNew }
        ]
      },
      {
        title: 'Payment Info',
        icon: ICONS.card,
        path: PATH_LINGUIST.user.paymentInfo,
        // path: PATH_LINGUIST.user.root,
        roles: 'Linguist'
      },
      {
        title: 'Projects',
        icon: ICONS.projects,
        path: PATH_LINGUIST.user.projects,
        // path: PATH_LINGUIST.user.root,
        roles: 'Linguist'
      },
      //   {
      //     title: 'Translation Projects',
      //     icon: ICONS.translationProject,
      //     path: PATH_LINGUIST.user.translationProjects,
      //     roles: "Linguist",
      //   },
      //   {
      //   title: 'Interpretation Projects',
      //   icon: ICONS.interpretationProject,
      //   path: PATH_LINGUIST.user.interpretationProjects,
      //   roles: "Linguist",
      // },
      //   {
      //     title: 'Proofreading Projects',
      //     icon: ICONS.proofReading,
      //     path: PATH_LINGUIST.user.prMenu,
      //     roles: "Linguist",
      //   },
      // {
      //   title: 'Invoices',
      //   icon: ICONS.invoice,
      //   path: PATH_LINGUIST.technician.root,
      //   roles: 'Technician',
      //   children: [
      //     { title: 'list', path: PATH_LINGUIST.technician.invoice },
      //     { title: 'create', path: PATH_LINGUIST.technician.invoiceNew },
      //   ],
      // },

      {
        title: 'Technician Sheets',
        icon: ICONS.sheets,
        path: PATH_LINGUIST.technician.technicianSheetsList,
        roles: 'Technician'
      },
      {
        title: 'Inventory',
        icon: ICONS.ecommerce,
        path: PATH_LINGUIST.technician.inventory,
        roles: 'Technician'
      },
      {
        title: 'Formatting Projects',
        icon: ICONS.formatting,
        path: PATH_LINGUIST.formatter.formattingFilesList,
        roles: 'Formatter'
      },
      {
        title: 'Projects Calendar',
        icon: ICONS.calendar,
        path: PATH_LINGUIST.formatter.projectsCalender,
        roles: 'Formatter'
      },
      {
        title: 'Marketing channels',
        icon: ICONS.marketingAnalysis,
        path: PATH_LINGUIST.marketeer.marketingChannelList,
        roles: 'Marketeer'
      },
      {
        title: 'Home',
        icon: ICONS.home,
        path: PATH_CLIENT.client.dashboard,
        // path:"/clients/dashboard",
        roles: 'Client'
      },
      // {
      //   title: 'Barzano Calender',
      //   icon: ICONS.dashboard,
      //   path: PATH_CLIENT.client.barzanoCalendar,
      //   roles: "Client",
      // },
      {
        title: 'Edit Profile',
        icon: ICONS.clientProfile,
        path: PATH_CLIENT.client.editProfile,
        roles: 'Client'
      },
      {
        title: 'Instant Quote',
        icon: ICONS.quote,
        path: PATH_CLIENT.client.instantQuote,
        roles: 'Client'
      },
      {
        title: 'Orders',
        icon: ICONS.orders,
        path: PATH_CLIENT.client.orders,
        roles: 'Client'
      },
      // BLOG — the portal's news and the software team's own notes (called
      // Articles until 12/10/2026). The list and the form are the software
      // team's; everyone else meets a post through the dashboard carousel.
      {
        title: 'Blog',
        icon: ICONS.blog,
        roles: ['admin', 'Software Developer'],
        path: PATH_DASHBOARD.blog.root,
        children: [
          { title: 'list', path: PATH_DASHBOARD.blog.posts },
          { title: 'new post', path: PATH_DASHBOARD.blog.new }
        ]
      },
      {
        title: 'Settings',
        icon: ICONS.settings,
        roles: ['admin', 'Software Developer'],
        path: PATH_DASHBOARD.settings.root,

        children: [
          {
            title: 'Communication',
            icon: ICONS.communication,
            path: PATH_DASHBOARD.settings.communication.root,
            children: [
              {
                title: 'Email Templates',
                path: PATH_DASHBOARD.settings.communication.emailTemplateList
              },
              {
                title: 'Mailboxes & sync',
                path: PATH_DASHBOARD.settings.communication.mailboxes
              },
              // The email conversations with clients (sent from Universe,
              // answered in Outlook); the record pages carry their own panel.
              {
                title: 'Conversations',
                path: PATH_DASHBOARD.communications.root
              }
            ]
          },
          {
            title: 'KPI Management',
            icon: ICONS.analytics,
            path: PATH_DASHBOARD.settings.kpi.list
          }
          // {
          //   title: 'Service Management',
          //   icon: ICONS.analytics,
          //   path: PATH_DASHBOARD.settings.service.list,
          //   roles: ['admin']
          // }
        ]
      }
    ]
  }
];

export default navConfig;
